
import React, { useEffect, useState, useContext } from 'react'
import { Layout, Card, Typography, Spin, Button, Divider, Row, Col, Tag } from 'antd'
import { ArrowLeftOutlined, PaperClipOutlined } from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import { AuthContext } from '../auth/AuthContext'
import { axiosForm } from '../../axiosInstances'
import dayjs from 'dayjs'
import DashboardLayout from '../dashboard/DashboardLayout'

const { Content } = Layout
const { Title, Text } = Typography

const formatUserFriendlyDate = (isoDate) =>
  isoDate ? dayjs(isoDate).format('DD/MM/YYYY, HH:mm') : ''

const InfoRow = ({ label, value }) => (
  <div style={{ marginBottom: 4 }}>
    <Text strong>{label}: </Text>
    <Text>{value || <span style={{ color: "#bbb" }}>-</span>}</Text>
  </div>
)

const percentAndDays = (value, days) =>
  value
    ? <>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#1890ff" }}>{value} %</div>
        {days && <div style={{ color: "#888", fontSize: 14 }}>{days} jours</div>}
      </>
    : <span style={{ color: "#bbb", fontSize: 16 }}>-</span>

// Calculate totals
const calculateTotals = (items, tva) => {
  let totalHT = 0;
  items.forEach(item => {
    if (item.is_available && item.quoted_total_price) {
      totalHT += parseFloat(item.quoted_total_price) || 0;
    } else if (item.has_alternative && item.alternative_total_price) {
      totalHT += parseFloat(item.alternative_total_price) || 0;
    }
  });
  const tvaValue = tva ? parseFloat(tva) : 20;
  const totalTTC = totalHT + (totalHT * tvaValue / 100);
  return { totalHT, totalTTC };
};

const ArticleCard = ({ item, currencyName }) => {
  // Determine border and tag color
  const borderColor = item.is_available
    ? "#52c41a"
    : "#ff4d4f";
  const tagColor = item.is_available
    ? "success"
    : "error";

  return (
    <Card
      size="small"
      variant="outlined"
      style={{
        borderLeft: `6px solid ${borderColor}`,
        marginBottom: 18,
        boxShadow: "0 2px 6px #f0f1f2",
        background: "#fff",
      }}
      styles={{ body: { padding: 18 } }}
    >
      <Row align="middle" gutter={24}>
        <Col flex="auto">
          <div style={{ fontWeight: 600, fontSize: 17, color: "#222" }}>
            {item.name}
            {item.reference && <span style={{ fontWeight: 400, color: "#888" }}> {item.reference}</span>}
            {item.brand && <span style={{ fontWeight: 400, color: "#888", marginLeft: 8 }}>({item.brand})</span>}
            <Tag color={tagColor} style={{ marginLeft: 8 }}>
              {item.is_available
                ? "Disponible"
                : item.has_alternative
                  ? "Non disponible"
                  : "Non disponible"}
            </Tag>
          </div>
          {item.description && (
            <div style={{ color: "#666", fontSize: 14, marginTop: 4 }}>{item.description}</div>
          )}

          {/* Only show quantity/price/total/delai if available */}
          {item.is_available && (
            <>
              <div style={{ fontSize: 15, marginTop: 8 }}>
                <b>Quantité :</b> {item.quantity}
              </div>
              <div style={{ fontSize: 14, marginTop: 2 }}>
                <b>Délai de livraison :</b> {item.delivery_time ? `${item.delivery_time} jours` : <span style={{ color: "#bbb" }}>-</span>}
              </div>
              <div style={{ fontSize: 15, marginTop: 4 }}>
                <b>Prix unitaire :</b>{" "}
                {item.quoted_unit_price
                  ? <span style={{ color: "#1890ff" }}>{item.quoted_unit_price} {currencyName}</span>
                  : <span style={{ color: "#bbb" }}>-</span>
                }
              </div>
              <div style={{ fontSize: 15, marginTop: 4 }}>
                <b>Total :</b>{" "}
                {item.quoted_total_price
                  ? <span style={{ color: "#1890ff" }}>{item.quoted_total_price} {currencyName}</span>
                  : <span style={{ color: "#bbb" }}>-</span>
                }
              </div>
            </>
          )}

          {item.vendor_comments && (
            <div style={{ marginTop: 6, fontStyle: 'italic', color: "#888" }}>
              <b>Commentaire :</b> {item.vendor_comments}
            </div>
          )}
        </Col>
      </Row>
      {/* Alternative Article */}
      {item.has_alternative &&
        <Card
          size="small"
          variant="outlined"
          style={{
            borderLeft: "6px solid #faad14",
            marginTop: 16,
            marginBottom: 0,
            background: "#fcfcf4",
            boxShadow: "0 1px 3px #f0f1f2"
          }}
          styles={{ body: { padding: 14 } }}
        >
          <div style={{ fontWeight: 600, fontSize: 15, color: "#b8860b" }}>
            {item.alternative_reference && (
              <>
                {item.alternative_reference}
                {item.alternative_brand && (
                  <span style={{ fontWeight: 400, color: "#888", marginLeft: 8 }}>
                    ({item.alternative_brand})
                  </span>
                )}
              </>
            )}
            {!item.alternative_reference && item.alternative_brand && (
              <span style={{ fontWeight: 400, color: "#888" }}>
                ({item.alternative_brand})
              </span>
            )}
            <Tag color="gold" style={{ marginLeft: 8 }}>Alternative</Tag>
          </div>
          {item.alternative_description && (
            <div style={{ color: "#888", fontSize: 14, marginTop: 2 }}>
              {item.alternative_description}
            </div>
          )}
          <div style={{ fontSize: 14, marginTop: 2 }}>
            <b>Quantité :</b> {item.alternative_quantity || <span style={{ color: "#bbb" }}>-</span>}
          </div>
          <div style={{ fontSize: 14, marginTop: 2 }}>
            <b>Délai de livraison :</b> {item.alternative_delivery_time ? `${item.alternative_delivery_time} jours` : <span style={{ color: "#bbb" }}>-</span>}
          </div>
          <div style={{ fontSize: 15, marginTop: 4 }}>
            <b>Prix unitaire :</b>{" "}
            {item.alternative_unit_price
              ? <span style={{ color: "#b8860b" }}>{item.alternative_unit_price} {currencyName}</span>
              : <span style={{ color: "#bbb" }}>-</span>
            }
          </div>
          <div style={{ fontSize: 15, marginTop: 4 }}>
            <b>Total :</b>{" "}
            {item.alternative_total_price
              ? <span style={{ color: "#b8860b" }}>{item.alternative_total_price} {currencyName}</span>
              : <span style={{ color: "#bbb" }}>-</span>
            }
          </div>
          {item.alternative_comments && (
            <div style={{ marginTop: 6, fontStyle: 'italic', color: "#888" }}>
              <b>Commentaire :</b> {item.alternative_comments}
            </div>
          )}
        </Card>
      }
    </Card>
  );
};

const HistoryDetails = () => {
  const { rfqId } = useParams()
  const { token } = useContext(AuthContext)
  const [loading, setLoading] = useState(true)
  const [rfq, setRfq] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (!rfqId || !token) return
    setLoading(true)
    
    axiosForm.get(`/authentication/api/rfq/${rfqId}/`)
      .then(response => {
        setRfq(response.data)
        setLoading(false)
      })
      .catch(error => {
        console.error('Error fetching RFQ details:', error)
        setLoading(false)
      })
  }, [rfqId, token])

  // Calculate totals once RFQ is loaded
  const { totalHT, totalTTC } = rfq?.items ? calculateTotals(rfq.items, rfq.tva) : { totalHT: 0, totalTTC: 0 };

  return (
    <DashboardLayout selectedMenuKey="history">
      <Content style={{ margin: '40px 24px', overflow: 'auto' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          style={{ marginBottom: 20 }}
          onClick={() => navigate('/history')}
        >
          Retour à l'historique
        </Button>
        <Card
          style={{ boxShadow: '0 2px 8px #f0f1f2', position: 'relative', maxWidth: 1100, margin: '0 auto' }}
          styles={{ body: { paddingTop: 30, paddingBottom: 24, paddingLeft: 24, paddingRight: 24 } }}
        >
          {loading ? (
            <Spin size="large" />
          ) : !rfq ? (
            <Text type="danger">Erreur de chargement du RFQ</Text>
          ) : (
            <>
              {/* Title */}
              <Title level={4} style={{ marginBottom: 0 }}>
                RFQ <span style={{ color: '#0958d9' }}>{rfq.rfq_number}</span>
              </Title>
              <Divider style={{ marginTop: 8, marginBottom: 16 }} />

              {/* General Info */}
              <div style={{ marginBottom: 16 }}>
                <InfoRow label="Date de réception" value={formatUserFriendlyDate(rfq.sent_date)} />
                <InfoRow label="Date de soumission" value={formatUserFriendlyDate(rfq.vendor_quote?.created_at)} />
                <InfoRow label="Date d'échéance" value={formatUserFriendlyDate(rfq.due_date)} />
              </div>

              {/* Top Totals Row */}
              <Row gutter={[16, 16]} style={{ marginBottom: 14 }}>
                <Col xs={24} sm={6} flex={1}>
                  <Card size="small" variant="outlined" style={{ background: "#f7f9fa", borderColor: "#d9d9d9", minHeight: 130 }} styles={{ body: { padding: 10 } }}>
                    <div style={{ fontSize: 14, color: "#555", fontWeight: 600 }}>Total HT</div>
                    <div style={{ fontSize: 19, fontWeight: 700 }}>
                      {totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {rfq.currency_code}
                    </div>
                    <div style={{ fontSize: 14, color: "#555", fontWeight: 600, marginTop: 8 }}>Total TTC</div>
                    <div style={{ fontSize: 19, fontWeight: 700 }}>
                      {totalTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {rfq.currency_code}
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={6} flex={1}>
                  <Card size="small" variant="outlined" style={{ background: "#f7f9fa", borderColor: "#d9d9d9", minHeight: 130 }} styles={{ body: { padding: 10 } }}>
                    <div style={{ fontSize: 14, color: "#555", fontWeight: 600 }}>Devise</div>
                    <div style={{ fontSize: 19, fontWeight: 700 }}>{rfq.currency_code}</div>
                  </Card>
                </Col>
                <Col xs={24} sm={6} flex={1}>
                  <Card size="small" variant="outlined" style={{ background: "#f7f9fa", borderColor: "#d9d9d9", minHeight: 130 }} styles={{ body: { padding: 10 } }}>
                    <div style={{ fontSize: 14, color: "#555", fontWeight: 600 }}>Plafond</div>
                    <div style={{ fontSize: 19, fontWeight: 700 }}>{rfq.order_ceiling || <span style={{ color: "#bbb" }}>-</span>}</div>
                  </Card>
                </Col>
                <Col xs={24} sm={6} flex={1}>
                  <Card size="small" variant="outlined" style={{ background: "#f7f9fa", borderColor: "#d9d9d9", minHeight: 130 }} styles={{ body: { padding: 10 } }}>
                    <div style={{ fontSize: 14, color: "#555", fontWeight: 600 }}>TVA</div>
                    <div style={{ fontSize: 19, fontWeight: 700 }}>{rfq.tva} %</div>
                  </Card>
                </Col>
              </Row>
              <div style={{ fontSize: 13, color: "#888", marginBottom: 12 }}>
                ⚠️ Le total inclut le prix des articles originaux ou, le cas échéant, le prix des équivalents (alternatives proposées).
              </div>

              <Divider />

              {/* Articles List */}
              <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>Articles proposés</Title>
              <div>
                {rfq.items && rfq.items.length > 0 ? rfq.items.map(item => (
                  <ArticleCard key={item.id} item={item} currencyName={rfq.currency_code} />
                )) : <Text>Aucun article proposé</Text>}
              </div>

              <Divider />

              {/* Commercial Conditions as cards */}
              <Title level={5} style={{ marginTop: 28, marginBottom: 18 }}>Conditions commerciales</Title>
              <Row gutter={[24, 24]} style={{ marginBottom: 18 }}>
                <Col xs={24} md={8}>
                  <Card size="large" variant="outlined" style={{ background: "#fafbfc", borderColor: "#d9d9d9" }} styles={{ body: { padding: 14 } }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#444", marginBottom: 7 }}>
                      Paiement à la commande
                    </div>
                    {percentAndDays(rfq.payment_at_command, rfq.payment_at_command_days)}
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="large" variant="outlined" style={{ background: "#fafbfc", borderColor: "#d9d9d9" }} styles={{ body: { padding: 14 } }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#444", marginBottom: 7 }}>
                      Paiement à la livraison
                    </div>
                    {percentAndDays(rfq.payment_at_delivery, rfq.payment_at_delivery_days)}
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="large" variant="outlined" style={{ background: "#fafbfc", borderColor: "#d9d9d9" }} styles={{ body: { padding: 14 } }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#444", marginBottom: 7 }}>
                      Paiement à la réception
                    </div>
                    {percentAndDays(rfq.payment_at_receipt, rfq.payment_at_receipt_days)}
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="large" variant="outlined" style={{ background: "#fafbfc", borderColor: "#d9d9d9" }} styles={{ body: { padding: 14 } }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: "#444", marginBottom: 7 }}>
                      Validité de l'offre
                    </div>
                    <div style={{ fontSize: 17 }}>
                      {rfq.vendor_quote?.validity_period
                        ? `${rfq.vendor_quote.validity_period} jours`
                        : <span style={{ color: "#bbb" }}>-</span>
                      }
                    </div>
                  </Card>
                </Col>
                <Col xs={24} md={16}>
                  <Card size="large" variant="outlined" style={{ background: "#fafbfc", borderColor: "#d9d9d9" }} styles={{ body: { padding: 14 } }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: "#444", marginBottom: 7 }}>
                      Commentaires fournisseur
                    </div>
                    <div style={{ fontSize: 16 }}>{rfq.vendor_quote?.vendor_notes || <span style={{ color: "#bbb" }}>-</span>}</div>
                  </Card>
                </Col>
              </Row>

              {/* Documents */}
              {rfq.documents && rfq.documents.length > 0 && (
                <>
                  <Divider />
                  <Title level={5} style={{ marginTop: 28 }}>Documents joints</Title>
                  <ul>
                    {rfq.documents.map(doc => (
                      <li key={doc.id}>
                        <a href={doc.file} target="_blank" rel="noopener noreferrer">
                          <PaperClipOutlined /> {doc.original_filename || "Document"}
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </Card>
      </Content>
    </DashboardLayout>
  )
}

export default HistoryDetails
